import React, { useState, useEffect } from 'react';
import {
  DataTable,
  Column,
  Button,
  Tag,
  Dropdown,
  Pagination,
  ButtonType,
  ButtonSize,
  TagVariant,
  DropdownType,
  DropdownSize,
  IconName,
  DropdownOption,
} from '@hills-design-system/components';
import SectionTitle from '../../../components/SectionTitle/SectionTitle';
import { useTranslation } from '../../../hooks/useTranslation';
import { TranslationKeyEnum } from '../../../types/constants';
import {
  useAdoptionReportState,
  useAdoptionReportActions,
} from '../../../hooks/useAdoptionReports';
import {
  AdoptionReport,
  PaginatedAdoptionReports,
  AdoptionCounts,
} from '../../../types/adoptionReports';
import { ReportStatus } from '../../../types/reports';
import { formatTimeframeFromWeekOf } from '../../../utils/timeframeHelpers';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import {
  formatDueDateLabel,
  getDueDateTagVariant,
  utcCalendarDateToLocal,
} from '../../../utils/dateFormatHelpers';
import AdoptionReportModal from '../../../components/Modal/AdoptionReportModal';
import Banner from '../../../components/Banner/Banner';
import { Tooltip } from '../../../components/Tooltip';
import { useSearchParams } from 'react-router-dom';
import InitialReportingTable from './shared/InitialReportingTable';
import { analytics } from '../../../services/analytics';
import { useShelterState } from '../../../hooks/useShelter';
import { BoltIcon } from '../../../components/icons';

const Adoptions: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<AdoptionReport | null>(null);
  const [hasInitialData, setHasInitialData] = useState<boolean | null>(null);

  const { t } = useTranslation();
  const { shelter } = useShelterState();
  const isPetszelEnabled = shelter?.is_petszel_enabled === true;
  const petszelFirstUsedAt = shelter?.petszel_first_used_at;
  const itemsPerPage = 10;

  // Helper function to check if a report falls within the petszel-managed window
  const isPetszelManagedReport = (timeframeStart: string): boolean => {
    if (!petszelFirstUsedAt) return false;
    const firstUsedDate = new Date(petszelFirstUsedAt);
    const reportDate = new Date(timeframeStart);

    // Compare only year and month
    const firstUsedYear = firstUsedDate.getFullYear();
    const firstUsedMonth = firstUsedDate.getMonth();
    const reportYear = reportDate.getFullYear();
    const reportMonth = reportDate.getMonth();

    return reportYear > firstUsedYear ||
      (reportYear === firstUsedYear && reportMonth >= firstUsedMonth);
  };

  useEffect(() => {
    // set page title
    document.title = t(TranslationKeyEnum.AdoptionHeader);
  }, [t]);

  // API hooks
  const adoptionReports: PaginatedAdoptionReports | null = useAdoptionReportState();
  const { getAdoptionReports, submitAdoptionReport, updateAdoptionReport } =
    useAdoptionReportActions();

  const loading = adoptionReports === null;

  // Fetch data on component mount and when filters change
  useEffect(() => {
    getAdoptionReports(selectedFilter, currentPage, itemsPerPage);
  }, [getAdoptionReports, selectedFilter, currentPage]);

  // Track initial data state when we get data with 'all' filter
  useEffect(() => {
    if (adoptionReports !== null && selectedFilter === 'all' && hasInitialData === null) {
      setHasInitialData(adoptionReports.data.length > 0);
    }
  }, [adoptionReports, selectedFilter, hasInitialData]);

  const filterOptions: DropdownOption[] = [
    { id: 'all', label: t(TranslationKeyEnum.AllReports) },
    { id: 'submitted', label: t(TranslationKeyEnum.Submitted) },
    { id: 'missed', label: t(TranslationKeyEnum.Missed) },
    { id: 'late', label: t(TranslationKeyEnum.Late) },
  ];

  // Use API data when available, fallback to mock data for development
  const displayData = adoptionReports?.data ?? [];
  const totalPages = adoptionReports?.totalPages ?? 0;

  // Modal handlers
  const openModal = (report: AdoptionReport) => {
    analytics.ctaClick('start', 'adoption report', report.timeframe);
    setSelectedReport(report);
    setIsModalOpen(true);
  };

  // Handle auto-opening modal when coming from home page
  useEffect(() => {
    const reportId = searchParams.get('reportId');
    if (reportId && adoptionReports?.data && !isModalOpen) {
      const targetReport = adoptionReports.data.find((report) => report.id === reportId);
      if (targetReport) {
        openModal(targetReport);
        // Remove the reportId parameter from URL after opening modal
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('reportId');
        setSearchParams(newSearchParams);
      }
    }
  }, [adoptionReports?.data, searchParams, setSearchParams, isModalOpen]);

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedReport(null);
  };

  // Report submission handlers
  const handleSubmitAdoptionReport = async (adoptionCounts: AdoptionCounts) => {
    if (!selectedReport) return;

    try {
      const result = await submitAdoptionReport({
        timeframe_start: selectedReport.timeframe_start,
        adoptionCounts,
      });

      if (result.success) {
        analytics.ctaClick('submit', 'adoption report', selectedReport.timeframe);
        getAdoptionReports(selectedFilter, currentPage, itemsPerPage);
      }
    } catch (error) {
      console.error('Error submitting adoption report:', error);
    }
  };

  const handleUpdateAdoptionReport = async (adoptionCounts: AdoptionCounts) => {
    if (!selectedReport) return;

    try {
      const result = await updateAdoptionReport(selectedReport.timeframe_start, {
        timeframe_start: selectedReport.timeframe_start,
        adoptionCounts,
      });

      if (result.success) {
        analytics.ctaClick('submit', 'adoption report', selectedReport.timeframe);
        getAdoptionReports(selectedFilter, currentPage, itemsPerPage);
      }
    } catch (error) {
      console.error('Error updating adoption report:', error);
    }
  };

  const handleFilterChange = (selectedId: string) => {
    setSelectedFilter(selectedId);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Define table columns using Hills Design System DataTable
  const columns: Column[] = [
    {
      header: t(TranslationKeyEnum.ReportTimeframe),
      accessor: 'timeframe',
      renderCell: (value: string) => formatTimeframeFromWeekOf(value),
    },
    {
      header: t(TranslationKeyEnum.Status),
      accessor: 'status',
      renderCell: (value: string, row: AdoptionReport) => {

        // Check if this report is in the petszel-managed window
        if (shelter?.is_petszel_enabled && isPetszelManagedReport(row.timeframe_start)) {
          return <Tag label={t(TranslationKeyEnum.AutomatedSync)} variant={TagVariant.Green} />;
        }

        // Show existing statuses for months before petszel_first_used_at
        if (value === ReportStatus.Due) {
          const dueDateLabel = formatDueDateLabel(row.dueDate, t);
          const tagVariant = getDueDateTagVariant(value, row.isWarning);
          return <Tag label={dueDateLabel} variant={tagVariant} />;
        } else if (value === ReportStatus.Submitted) {
          return <Tag label={t(TranslationKeyEnum.Submitted)} variant={TagVariant.Green} />;
        } else if (value === ReportStatus.Missed) {
          return <Tag label={t(TranslationKeyEnum.Missed)} variant={TagVariant.Red} />;
        } else if (value === ReportStatus.Late) {
          // Late submissions displayed in green per spec
          return <Tag label={t(TranslationKeyEnum.LateSubmission)} variant={TagVariant.Green} />;
        }
        return value;
      },
    },
    {
      header: t(TranslationKeyEnum.TotalAdoptions),
      accessor: 'totalAdoptions',
      renderCell: (value: number, row: AdoptionReport) => {
        // Check if report was not submitted (Due or Missed status)
        if (row.status === ReportStatus.Due || row.status === ReportStatus.Missed) {
          if (shelter?.is_petszel_enabled && isPetszelManagedReport(row.timeframe_start)) {
            return t(TranslationKeyEnum.AdoptionCount, value);
          }
          return '--';
        }

        // Report was submitted (Submitted or Late) - show actual value including 0
        return t(TranslationKeyEnum.AdoptionCount, value);

      },
    },
    {
      header: t(TranslationKeyEnum.LastModified),
      accessor: 'lastModified',
      renderCell: (value: string | undefined, row: AdoptionReport) => {
        // Check if report was not submitted (Due or Missed status)
        if (row.status === ReportStatus.Due || row.status === ReportStatus.Missed) {
          return '--';
        }

        // Report was submitted (Submitted or Late) - format the date
        if (!value) return '--';
        const dateObj = utcCalendarDateToLocal(value);
        if (!dateObj) return value;
        return format(dateObj, 'MMM. d, yyyy');
      },
    },
    ...(isPetszelEnabled
      ? []
      : [
        {
          header: (
            <span className="sr-only">Actions</span>
          ) as unknown as string,
          accessor: 'actions',
          align: 'right' as const,
          renderCell: (_value: unknown, row: AdoptionReport) => {
            // For missed reports: show tooltip if late submission window is closed (canSubmit = false)
            // or show Start button if late submission window is still open (canSubmit = true)
            if (row.status === ReportStatus.Missed) {
              if (row.canSubmit === true) {
                // Late submission window is still open - show Start button
                return (
                  <Button
                    label={t(TranslationKeyEnum.Start)}
                    type={ButtonType.Primary}
                    size={ButtonSize.Small}
                    onClick={() => openModal(row)}
                    className="!w-32"
                  />
                );
              } else {
                // Late submission window is closed - show tooltip
                return (
                  <Tooltip
                    align="left"
                    content={
                      <div className="w-[360px]  p-4">
                        <div className="text-sm font-extralight text-white leading-[1.375]">
                          {t(TranslationKeyEnum.DeadlinePassedTooltip)}
                        </div>
                      </div>
                    }
                  />
                );
              }
            }

            // For submitted reports, only show button if canEdit is true
            if (row.status === ReportStatus.Submitted && !row.canEdit) {
              return null;
            }

            // Show button for due and submitted (with canEdit) reports
            if (row.status === ReportStatus.Due || row.canEdit) {
              const buttonLabel = row.canEdit
                ? t(TranslationKeyEnum.Edit)
                : t(TranslationKeyEnum.Start);
              const buttonType = row.canEdit ? ButtonType.Secondary : ButtonType.Primary;

              return (
                <Button
                  label={buttonLabel}
                  type={buttonType}
                  size={ButtonSize.Small}
                  onClick={() => openModal(row)}
                  className="!w-32"
                />
              );
            }

            // Default: no button
            return null;
          },
        },
      ]),
  ];

  // Filter missed reports for banner
  const missedReportsAll: AdoptionReport[] = displayData.filter(
    (report) => report.status === ReportStatus.Missed && report.canSubmit === true
  );

  // Identify a due-soon report (<= 3 days to deadline) for yellow warning banner
  const dueSoonReport: AdoptionReport | undefined = displayData.find(
    (report) => report.status === ReportStatus.Due && report.isWarning === true
  );

  const getModalTitle = () => {
    if (!selectedReport) return t(TranslationKeyEnum.SubmitAdoptionReport);

    const monthDisplay = formatTimeframeFromWeekOf(selectedReport.timeframe);

    if (selectedReport.status === ReportStatus.Submitted && selectedReport.canEdit) {
      return t(TranslationKeyEnum.AddMoreMonthAdoptions, monthDisplay);
    } else if (selectedReport.status === ReportStatus.Due && selectedReport.totalAdoptions > 0) {
      return t(TranslationKeyEnum.AddMoreMonthAdoptions, monthDisplay);
    } else {
      return t(TranslationKeyEnum.SubmitMonthAdoptions, monthDisplay);
    }
  };

  return (
    <div className="w-full bg-white rounded-2xl border border-black/20 p-12">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2">
          <SectionTitle title={t(TranslationKeyEnum.AdoptionHeader)} subtitle="" />
          <Tooltip
            content={
              <div className="w-[360px]  p-4 flex flex-col gap-4">
                {
                  // manual shelter message
                  shelter && !shelter.is_petszel_enabled && (
                    <>
                      <div>
                        {t(TranslationKeyEnum.AdoptionTooltipPrefix)}{' '}
                        {t(TranslationKeyEnum.AdoptionTooltipLinkText)}
                        {/* temporarily commenting until we have the document link */}
                        {/* <a href="#" className="underline">
                    {t(TranslationKeyEnum.AdoptionTooltipLinkText)}
                  </a> */}
                      </div>
                      <div>{t(TranslationKeyEnum.AdoptionTooltipSuffix)}</div>
                    </>
                  )
                }
                {
                  // petszel shelter message
                  shelter && shelter.is_petszel_enabled && (
                    <div className="flex items-start gap-2">
                      <BoltIcon width={10} height={15} fill="white" className="mt-0.5 flex-shrink-0" />
                      <div className="text-sm font-normal text-white leading-[1.375]">
                        {t(TranslationKeyEnum.PetszelAdoptionReportTooltipTitle)}
                      </div>
                    </div>
                  )
                }
              </div>
            }
          />
        </div>

        {
          !isPetszelEnabled &&
          <div style={{ position: 'relative', width: '320px' }}>
            <Dropdown
              ariaLabel={t(TranslationKeyEnum.FilterReportsByStatusAriaLabel)}
              placeholder={t(TranslationKeyEnum.AllReports)}
              options={filterOptions}
              selectedId={selectedFilter}
              onChange={handleFilterChange}
              type={DropdownType.Secondary}
              size={DropdownSize.Medium}
              prefixIcon={IconName.Tune}
              className="!mb-0"
            />
          </div>
        }
      </div>

      {!loading && !isPetszelEnabled && missedReportsAll.length > 0 && (
        <div className="mb-6">
          { <Banner
            variant="danger"
            title={
              missedReportsAll.length === 1
                ? t(TranslationKeyEnum.MissedReportSingular)
                : t(TranslationKeyEnum.MissedReportPlural, missedReportsAll.length)
            }
            subtitle={t(TranslationKeyEnum.SubmitNowToStayInGoodStanding)}
            className="mb-6"
          /> }
        </div>
      )}

      {!loading && !isPetszelEnabled && dueSoonReport && (
        <div className="mb-6">
          <Banner
            variant="warning"
            title={t(TranslationKeyEnum.SubmitAdoptionsByDate, format(utcCalendarDateToLocal(dueSoonReport.timeframe_start) ?? new Date(), 'MMMM'), dueSoonReport.dueDate ? format(toZonedTime(dueSoonReport.dueDate, 'UTC'), 'MMM. d') : '')}
            className="mb-6"
          />
        </div>
      )}
      {(() => {
        if (loading) {
          return (
            <div className="flex justify-center items-center h-40">{t(TranslationKeyEnum.LoadingAdoptionReports)}</div>
          );
        } else if (hasInitialData === true) {
          return <DataTable columns={columns} data={displayData} />;
        } else if (selectedFilter !== 'all') {
          // If a filter is active and there's no initial data, show empty table
          return <DataTable columns={columns} data={[]} />;
        } else {
          return (
            <InitialReportingTable
              header={(() => {
                // Use firstExpectedReport data from backend API if available
                if (adoptionReports?.firstExpectedReport) {
                  const reportMonth = adoptionReports.firstExpectedReport.timeframe;
                  const submissionStart = utcCalendarDateToLocal(
                    adoptionReports.firstExpectedReport.submissionWindowStart
                  );
                  const submissionMonth = submissionStart ? format(submissionStart, 'MMMM') : '';

                  return t(
                    TranslationKeyEnum.NoAdoptionReportsHeader,
                    reportMonth,
                    submissionMonth,
                    submissionMonth
                  );
                }

                // Fallback to showing generic message if firstExpectedReport is not available
                return t(TranslationKeyEnum.NoAdoptionReportsDescription);
              })()}
              description={t(TranslationKeyEnum.NoAdoptionReportsDescription)}
            />
          );
        }
      })()}

      {displayData.length > 0 && (
        <div className="flex justify-center mt-12">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            type="numeric"
          />
        </div>
      )}

      {/* Adoption Report Modal */}
      <AdoptionReportModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSubmit={
          selectedReport?.status === ReportStatus.Submitted && selectedReport?.canEdit
            ? handleUpdateAdoptionReport
            : handleSubmitAdoptionReport
        }
        timeframeStart={selectedReport?.timeframe_start ?? ''}
        lastEdited={selectedReport?.lastModified ?? ''}
        initialCounts={selectedReport?.adoptionCounts}
        title={getModalTitle()}
        isEditing={
          selectedReport?.status === ReportStatus.Submitted && selectedReport?.canEdit === true
        }
      />
    </div>
  );
};

export default Adoptions;
